import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { useFirestoreConnect, isLoaded } from 'react-redux-firebase';

import {
  annotationPhotoDocumentsDataSourceChangeInit,
  annotationPhotoDocumentsDataSourceChange,
} from '../../actions/Annotation/AnnotationActions';

import LoadingData from '../LoadingData';
import NoResults from '../NoResults';
import Select from '../Select';
import DataSourceManage from '../DataSourceAdd';
import PhotoDocument from '../PhotoDocument';
import PhotoDocumentDetails from '../PhotoDocumentDetails';

import { useTheme } from '../../ThemeContext';

import {
  Wrapper,
  Holder,
  Panel,
  ContentHolder,
  Header,
  Title,
  Content,
  Button,
  ActionButton,
  ActionButtonIcon,
  Category,
  CategoryTitle,
  CategoryValues,
  CategoryValue,
  Selectbox,
  Box,
} from './MultiAnnotation.style';

const MultiAnnotation = ({
  data = [],
  handleDeletePhotoDocument = () => {},
  handleSaveAnnotation = () => {},
  handleFinishAnnotation = () => {},
  handleChangeAnnotation = () => {},
  changedPhotoTagsAnnotation,
  selectedPhotoDocuments = [],
  handleSelectPhotoDocuments = () => {},
  isLoading = false,
  finishButtonMessage = '',
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const firebaseStore = useSelector((state) => state.firebase);
  const dispatch = useDispatch();
  const { profile } = firebaseStore;
  const isProfileCompany = profile.company && profile.company.id ? true : false;
  const [annotationDataSourceId, setAnnotationDataSourceId] = useState(
    undefined
  );
  const [visibleDataSourcesAddModal, setVisibleDataSourcesAddModal] = useState(
    false
  );
  const [selectedDataSource, setSelectedDataSource] = useState(undefined);
  const [visiblePhotoDocumentModal, setVisiblePhotoDocumentModal] = useState(
    false
  );

  const [selectedPhotoDocumentId, setSelectedPhotoDocumentId] = useState(
    undefined
  );
  const selectedPhotoDocument =
    selectedPhotoDocumentId &&
    data &&
    data.find((photoDocument) => photoDocument.id === selectedPhotoDocumentId);

  const [
    availableChangeLeftPhotoDocument,
    setAvailableChangeLeftPhotoDocument,
  ] = useState(false);
  const [
    availableChangeRightPhotoDocument,
    setAvailableChangeRightPhotoDocument,
  ] = useState(false);

  const dataSources = useSelector(
    (state) => state.firestore.ordered[`data_sources`]
  );
  const dataSource = useSelector(
    (state) =>
      annotationDataSourceId &&
      state.firestore.data[`data_sources/${annotationDataSourceId}`]
  );
  const dataSourcePhotoCategories = useSelector(
    (state) =>
      annotationDataSourceId &&
      state.firestore.ordered[
        `data_sources/${annotationDataSourceId}/photo_categories`
      ]
  );
  const dataSourceObjectsCategories = useSelector(
    (state) =>
      annotationDataSourceId &&
      state.firestore.ordered[
        `data_sources/${annotationDataSourceId}/objects_categories`
      ]
  );

  const handleChangeDataSource = async (dataSource) => {
    if (isProfileCompany) {
      const result = await dispatch(
        annotationPhotoDocumentsDataSourceChange({
          ids: data.map((photoDocument) => photoDocument.id),
          dataSourceId: dataSource,
        })
      );
      if (result) dispatch(annotationPhotoDocumentsDataSourceChangeInit());
    }
  };

  const handleOpenViewPhotoDocumentDetails = (id) => {
    setSelectedPhotoDocumentId(id);
    setVisiblePhotoDocumentModal(true);
  };

  const handleCloseViewPhotoDocumentDetails = () => {
    setVisiblePhotoDocumentModal(false);
    setSelectedPhotoDocumentId(undefined);
    setAvailableChangeLeftPhotoDocument(false);
    setAvailableChangeRightPhotoDocument(false);
  };

  const handleAction = (action = {}) => {
    if (action.type === 'EDIT') {
      setVisibleDataSourcesAddModal(true);
      setSelectedDataSource(action.payload);
    } else if (action.type === 'REJECT') {
      handleSaveAnnotation({ id: selectedPhotoDocumentId, isRejected: true });
    } else if (action.type === 'DELETE') {
      handleDeletePhotoDocument({ id: selectedPhotoDocumentId });
    } else {
      setVisibleDataSourcesAddModal(false);
      setSelectedDataSource(undefined);
    }
  };

  const handleChangePhotoDocument = (type) => {
    if (data && data.length > 0) {
      const photoDocumentIndex = data.findIndex(
        (photo) => String(photo.id) === String(selectedPhotoDocumentId)
      );
      if (photoDocumentIndex !== -1) {
        if (type === 'prev' && photoDocumentIndex > 0) {
          let newSelectedPhoto = data[photoDocumentIndex - 1];
          setSelectedPhotoDocumentId(newSelectedPhoto.id);
        } else if (type === 'next' && photoDocumentIndex < data.length - 1) {
          let newSelectedPhoto = data[photoDocumentIndex + 1];
          setSelectedPhotoDocumentId(newSelectedPhoto.id);
        }
      }
    }
  };

  const firestoreConnect = [];
  if (
    isProfileCompany &&
    !(annotationDataSourceId && isLoaded(dataSource) && dataSource)
  ) {
    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      subcollections: [
        {
          collection: 'data_sources',
        },
      ],
      storeAs: `data_sources`,
    });
  }
  if (isProfileCompany && annotationDataSourceId) {
    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      subcollections: [
        {
          collection: 'data_sources',
          doc: annotationDataSourceId,
        },
      ],
      storeAs: `data_sources/${annotationDataSourceId}`,
    });
    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      subcollections: [
        {
          collection: 'data_sources',
          doc: annotationDataSourceId,
          subcollections: [{ collection: 'photo_categories' }],
        },
      ],
      storeAs: `data_sources/${annotationDataSourceId}/photo_categories`,
    });
    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      subcollections: [
        {
          collection: 'data_sources',
          doc: annotationDataSourceId,
          subcollections: [{ collection: 'objects_categories' }],
        },
      ],
      storeAs: `data_sources/${annotationDataSourceId}/objects_categories`,
    });
  }
  useFirestoreConnect(firestoreConnect);

  useEffect(() => {
    if (data && data.length > 0)
      setAnnotationDataSourceId(data[0].data_source_id);
  }, [data]);

  useEffect(() => {
    if (data && data.length > 0) {
      const photoDocumentIndex = data.findIndex(
        (photo) => String(photo.id) === String(selectedPhotoDocumentId)
      );
      if (photoDocumentIndex !== -1) {
        setAvailableChangeLeftPhotoDocument(
          photoDocumentIndex > 0 ? true : false
        );
        setAvailableChangeRightPhotoDocument(
          photoDocumentIndex < data.length - 1 ? true : false
        );
      }
    }
    return () => {
      if (availableChangeLeftPhotoDocument)
        setAvailableChangeLeftPhotoDocument(false);
      if (availableChangeRightPhotoDocument)
        setAvailableChangeRightPhotoDocument(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPhotoDocumentId]);

  const renderCategoryBox = (category) => {
    return (
      <Category>
        <CategoryTitle theme={theme.annotation.categoryTitle}>
          {category.name || category.id}
        </CategoryTitle>
        <CategoryValues>
          {category.tags &&
            Object.entries(category.tags).map(([id, tag]) => {
              const changedAnnotationTag =
                changedPhotoTagsAnnotation &&
                changedPhotoTagsAnnotation[category.id];
              return (
                <CategoryValue
                  theme={theme.annotation.categoryValue}
                  key={id}
                  selected={
                    changedAnnotationTag
                      ? changedAnnotationTag === id
                        ? true
                        : undefined
                      : undefined
                  }
                  onClick={() => {
                    handleChangeAnnotation('PHOTO', {
                      categoryId: category.id,
                      tag: id,
                    });
                  }}
                >
                  {tag || id}
                </CategoryValue>
              );
            })}
        </CategoryValues>
      </Category>
    );
  };

  const renderDataSourceAssign = () => {
    return (
      <Selectbox>
        <Select
          options={
            dataSources &&
            dataSources.length > 0 &&
            dataSources.map((dataSource) => ({
              ...dataSource,
              value: dataSource.id,
              label: dataSource.name,
            }))
          }
          placeholderMessage={'ANNOTATION.ASSIGN_DATA_SOURCE'}
          onChange={(value) => handleChangeDataSource(value)}
          small
        />
      </Selectbox>
    );
  };

  const renderBox = () => {
    const isSelectedPhotoDocument = (id) =>
      selectedPhotoDocuments.find(
        (photoDocument) => String(photoDocument.id) === String(id)
      )
        ? true
        : false;

    return (
      <Box>
        {data.map(({ id, ...photoDocument }) => (
          <PhotoDocument
            key={id}
            data={{
              id,
              ...photoDocument,
            }}
            handleClick={(id) => handleOpenViewPhotoDocumentDetails(id)}
            isSelected={isSelectedPhotoDocument(id)}
            handleCheck={(photoDocument) =>
              handleSelectPhotoDocuments([
                ...selectedPhotoDocuments,
                photoDocument,
              ])
            }
            handleUncheck={(photoDocument) => {
              const { id } = photoDocument;
              handleSelectPhotoDocuments([
                ...selectedPhotoDocuments.filter(
                  (photoDocument) => String(photoDocument.id) !== String(id)
                ),
              ]);
            }}
          />
        ))}
      </Box>
    );
  };

  return (
    <>
      <Wrapper>
        <Holder theme={theme.annotation.holder} isBox={true}>
          {renderBox()}
        </Holder>
        <Holder>
          <Panel theme={theme.annotation.panel}>
            <ContentHolder theme={theme.photoDocumentDetails.contentHolder}>
              <Header theme={theme.photoDocumentDetails.header}>
                {annotationDataSourceId ? (
                  isLoaded(dataSource) ? (
                    dataSource ? (
                      <Title theme={theme.photoDocumentDetails.title}>
                        {dataSource.name}
                        {isLoaded(dataSourcePhotoCategories) &&
                        isLoaded(dataSourceObjectsCategories) ? (
                          <ActionButton
                            small
                            square
                            noMask
                            rounded
                            theme={theme.button.normal}
                            onClick={() =>
                              handleAction({
                                type: 'EDIT',
                                payload: {
                                  ...dataSource,
                                  id: annotationDataSourceId,
                                  photoCategories: dataSourcePhotoCategories,
                                  objectsCategories: dataSourceObjectsCategories,
                                },
                              })
                            }
                          >
                            <ActionButtonIcon className='material-icons-outlined'>
                              edit
                            </ActionButtonIcon>
                          </ActionButton>
                        ) : (
                          ''
                        )}
                      </Title>
                    ) : (
                      renderDataSourceAssign()
                    )
                  ) : (
                    <Title theme={theme.photoDocumentDetails.title}>
                      {intl.formatMessage({
                        id: 'ANNOTATION.LOADING_DATA_SOURCE',
                      })}
                    </Title>
                  )
                ) : (
                  renderDataSourceAssign()
                )}
                <Button
                  theme={theme.button.default}
                  widthAuto
                  noMask
                  small
                  onClick={() => handleSaveAnnotation()}
                  disabled={
                    isLoading ||
                    !(
                      selectedPhotoDocuments &&
                      selectedPhotoDocuments.length > 0
                    )
                  }
                >
                  {isLoading
                    ? intl.formatMessage({ id: 'ANNOTATION.PLEASE_WAIT' })
                    : intl.formatMessage({ id: 'ANNOTATION.SAVE' })}
                </Button>
              </Header>
              <Content>
                {annotationDataSourceId ? (
                  isLoaded(dataSource) &&
                  isLoaded(dataSourcePhotoCategories) &&
                  isLoaded(dataSourceObjectsCategories) ? (
                    dataSource ? (
                      <>
                        {dataSourcePhotoCategories &&
                          dataSourcePhotoCategories.length > 0 &&
                          dataSourcePhotoCategories
                            .filter(
                              (category) =>
                                category.id &&
                                category.tags &&
                                Object.values(category.tags).length > 0
                            )
                            .map((category, key) => (
                              <Fragment key={key}>
                                {renderCategoryBox(category)}
                              </Fragment>
                            ))}
                      </>
                    ) : (
                      <NoResults
                        fullmode
                        label={intl.formatMessage({
                          id: 'ANNOTATION.NO_DATA_SOURCE',
                        })}
                      />
                    )
                  ) : (
                    <LoadingData fullmode />
                  )
                ) : (
                  <NoResults
                    fullmode
                    label={intl.formatMessage({
                      id: 'ANNOTATION.NO_DATA_SOURCE',
                    })}
                  />
                )}
              </Content>
            </ContentHolder>
          </Panel>
          <Button
            theme={theme.button.default}
            onClick={() => handleFinishAnnotation()}
            disabled={isLoading}
          >
            {isLoading
              ? intl.formatMessage({ id: 'ANNOTATION.PLEASE_WAIT' })
              : finishButtonMessage
              ? finishButtonMessage
              : intl.formatMessage({ id: 'ANNOTATION.FINISH_ANNOTATING' })}
          </Button>
        </Holder>
      </Wrapper>
      {visibleDataSourcesAddModal && (
        <DataSourceManage
          data={selectedDataSource}
          handleClose={() => handleAction()}
        />
      )}
      {visiblePhotoDocumentModal && (
        <PhotoDocumentDetails
          data={selectedPhotoDocument}
          handleClose={() => handleCloseViewPhotoDocumentDetails()}
          handleActionPhotoDocument={(action) => handleAction(action)}
          handleChangePhotoDocument={(type) => handleChangePhotoDocument(type)}
          availableChangeLeftPhotoDocument={availableChangeLeftPhotoDocument}
          availableChangeRightPhotoDocument={availableChangeRightPhotoDocument}
          availableReject={true}
          availableDelete={true}
        />
      )}
    </>
  );
};

export const MultiDataAnnotation = ({
  data = [],
  handleDeletePhotoDocument = () => {},
  handleSaveAnnotation = () => {},
  handleFinishAnnotation = () => {},
  handleChangeAnnotation = () => {},
  changedPhotoTagsAnnotation,
  selectedPhotoDocuments = [],
  handleSelectPhotoDocuments = () => {},
  isLoading = false,
}) => {
  return (
    <MultiAnnotation
      data={data}
      handleDeletePhotoDocument={handleDeletePhotoDocument}
      handleSaveAnnotation={handleSaveAnnotation}
      handleFinishAnnotation={handleFinishAnnotation}
      handleChangeAnnotation={handleChangeAnnotation}
      changedPhotoTagsAnnotation={changedPhotoTagsAnnotation}
      selectedPhotoDocuments={selectedPhotoDocuments}
      handleSelectPhotoDocuments={handleSelectPhotoDocuments}
      isLoading={isLoading}
    />
  );
};

MultiAnnotation.propTypes = {
  data: PropTypes.array,
  handleDeletePhotoDocument: PropTypes.func,
  handleSaveAnnotation: PropTypes.func,
  handleFinishAnnotation: PropTypes.func,
  handleChangeAnnotation: PropTypes.func,
  changedPhotoTagsAnnotation: PropTypes.object,
  selectedPhotoDocuments: PropTypes.array,
  handleSelectPhotoDocuments: PropTypes.func,
  isLoading: PropTypes.bool,
  finishButtonMessage: PropTypes.string,
};

MultiDataAnnotation.propTypes = {
  data: PropTypes.array,
  handleDeletePhotoDocument: PropTypes.func,
  handleSaveAnnotation: PropTypes.func,
  handleFinishAnnotation: PropTypes.func,
  handleChangeAnnotation: PropTypes.func,
  changedPhotoTagsAnnotation: PropTypes.object,
  selectedPhotoDocuments: PropTypes.array,
  handleSelectPhotoDocuments: PropTypes.func,
  isLoading: PropTypes.bool,
};
